import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import Swal from 'sweetalert2'
import { useNotify } from '../../../provider/NotificationProvider'
import Breadcrumb from '../../../core/common/Breadcrumb/breadcrumb'
import ImageWithBasePath from '../../../core/common/imageWithBasePath'
import { Link } from 'react-router-dom'
import StudentSidebar from '../common/studentSidebar'
import ProfileCard from '../common/profileCard'
import { all_routes } from '../../router/all_routes'
import {
  calculateDiscount,
  downloadCertificate,
  getStoredCertificateName,
  prepareCertificateDownload,
  resolveCertificateSignatureDate,
  rupiahFormatter,
  setStoredCertificateName,
  CertificateMeta,
} from '../../../utils/helpers'
import { getPurchasedCourses } from '../../../api/user'
import { getAllCBTAttempt } from '../../../api/courses'

// Course card component
const CourseCard = ({ course, selectedItems, handleItemClick, type }: any) => {
  // Countdown state for live teaching
  const [countdown, setCountdown] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  })
  const [showCertificateModal, setShowCertificateModal] = useState(false)
  const [certificateInput, setCertificateInput] = useState('')
  const [bestCbtScore, setBestCbtScore] = useState<number | null>(null)
  const [storedCertificateName, setStoredCertificateNameState] = useState('')

  // Countdown effect for live teaching
  useEffect(() => {
    if (type === 'live' && course.start_date && course.end_date) {
      const timer = setInterval(() => {
        const now = new Date().getTime()
        const startDate = new Date(course.start_date).getTime()
        const timeLeft = startDate - now

        if (timeLeft > 0) {
          const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24))
          const hours = Math.floor(
            (timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
          )
          const minutes = Math.floor(
            (timeLeft % (1000 * 60 * 60)) / (1000 * 60),
          )
          const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000)

          setCountdown({ days, hours, minutes, seconds })
        } else {
          setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 })
        }
      }, 1000)

      return () => clearInterval(timer)
    }
  }, [course.start_date, course.end_date, type])

  const isCbt = type === 'cbt'
  const hasPosttest =
    isCbt ||
    course?.has_questions === 'yes' ||
    course?.has_questions === true ||
    Number(course?.questions_count || 0) > 0
  const attemptCount = Number(course?.attempt_count || 0)
  const rawMax = Number(course?.max_attempt)
  const maxAttempt = rawMax > 0 ? rawMax : 1
  const remainingAttempts = Math.max(maxAttempt - attemptCount, 0)
  const canRetry = attemptCount < maxAttempt
  const hasAttempt = attemptCount > 0
  const latestAttemptId = course?.latest_attempt_id
  const latestScore =
    course?.latest_score !== null && course?.latest_score !== undefined
      ? course.latest_score
      : null
  const isPassed = course?.is_passed === true

  const certificateName =
    course?.certificate_downloads?.name || storedCertificateName
  const detailLink =
    type === 'live'
      ? all_routes.studentLiveEventDetails.replace(':id', course.id.toString())
      : type === 'course'
      ? all_routes.studentCourseContent.replace(':id', course.id.toString())
      : type === 'cbt'
      ? all_routes.studentCBTList.replace(':id', String(course.id))
      : all_routes.courseDetails

  const signatureDate =
    type === 'live'
      ? resolveCertificateSignatureDate(
          course?.certificate_event_dates || undefined,
          course?.certificate_signature_date || course?.end_date || undefined,
        )
      : undefined
  const hasLiveTeachingCbt =
    type === 'live' && Number(course?.questions_count || 0) > 0

  const certificateMeta: CertificateMeta = {
    eventDates: course?.certificate_event_dates || undefined,
    jplTotal: course?.certificate_jpl_total || undefined,
    jplNote: course?.certificate_jpl_note || undefined,
    speakers: course?.certificate_speakers || undefined,
    materials: course?.certificate_materials || undefined,
    materialTopicOnly: (type === 'course' && !hasPosttest) || (type === 'live' && !hasLiveTeachingCbt),
    location: course?.certificate_location || undefined,
    signatureDate: signatureDate || undefined,
    certificateNumber: course?.certificate_downloads?.certificate_number || undefined,
    qrUrl: course?.certificate_downloads?.qr_url || undefined,
  }

  const getBestCbtScore = async (): Promise<number | null> => {
    if (!course?.id) return latestScore

    try {
      const res = await getAllCBTAttempt(String(course.id))
      const attempts = Array.isArray(res?.data) ? res.data : []

      const bestScore = attempts.reduce((best: number, attempt: any) => {
        const score = Number(attempt?.score)
        if (Number.isFinite(score)) {
          return Math.max(best, score)
        }
        return best
      }, Number.NEGATIVE_INFINITY)

      return bestScore === Number.NEGATIVE_INFINITY ? null : bestScore
    } catch (error) {
      console.error('Failed to fetch CBT attempts for best score:', error)
      return latestScore
    }
  }

  useEffect(() => {
    if (!hasPosttest) return
    if (!hasAttempt) {
      setBestCbtScore(null)
      return
    }

    let isActive = true

    const loadBestScore = async () => {
      const score = await getBestCbtScore()
      if (isActive) {
        setBestCbtScore(score)
      }
    }

    loadBestScore()

    return () => {
      isActive = false
    }
  }, [hasPosttest, hasAttempt, course?.id, latestScore])

  useEffect(() => {
    if (!hasPosttest || !hasAttempt || !certificateName || !course?.id) return
    if (
      course?.certificate_downloads?.certificate_number ||
      course?.certificate_downloads?.qr_url
    ) {
      return
    }

    const score =
      bestCbtScore !== null
        ? String(bestCbtScore)
        : latestScore !== null
        ? String(latestScore)
        : ''

    prepareCertificateDownload(
      Number(course.id),
      certificateName,
      score,
      course?.min_passing_score || '',
    )
  }, [
    hasPosttest,
    hasAttempt,
    certificateName,
    course?.id,
    course?.certificate_downloads?.certificate_number,
    course?.certificate_downloads?.qr_url,
    bestCbtScore,
    latestScore,
    course?.min_passing_score,
  ])

  const feedbackKey = `feedback_clicked_${course?.id}`
  const hasClickedFeedback = localStorage.getItem(feedbackKey) === 'true'

  const isDateFinished =
    type === 'live' && course?.end_date
      ? new Date().getTime() >= new Date(course.end_date).getTime()
      : true

  const isFeedbackRequiredAndPending = Boolean(
    course?.link_feedback &&
      String(course.link_feedback).trim() !== '' &&
      String(course.link_feedback).trim() !== 'null' &&
      String(course.link_feedback).trim() !== 'undefined' &&
      !hasClickedFeedback,
  )

  const isCbtRequiredAndPending = hasPosttest && !hasAttempt

  const isCertificateLocked =
    !isDateFinished || isFeedbackRequiredAndPending || isCbtRequiredAndPending

  const handleDownloadCertificate = async () => {
    if (type === 'live' && !isDateFinished) {
      Swal.fire({
        icon: 'warning',
        title: 'Kelas Belum Selesai',
        text: 'Sertifikat belum dapat didownload. Pastikan tanggal dan waktu kelas telah selesai.',
        confirmButtonColor: '#1d7092',
      })
      return
    }

    if (hasPosttest && !hasAttempt) {
      Swal.fire({
        icon: 'warning',
        title: 'Ujian CBT Belum Dikerjakan',
        text: 'Silakan kerjakan Ujian CBT / Post-Test minimal 1 kali terlebih dahulu sebelum mendownload sertifikat.',
        confirmButtonColor: '#1d7092',
      })
      return
    }

    if (isFeedbackRequiredAndPending) {
      Swal.fire({
        icon: 'warning',
        title: 'Feedback Diperlukan',
        text: 'Silakan isi Feedback Peserta terlebih dahulu sebelum mendownload sertifikat.',
        confirmButtonColor: '#1d7092',
      })
      return
    }

    if (!certificateName) {
      setCertificateInput('')
      setShowCertificateModal(true)
      return
    }

    const bestScore = await getBestCbtScore()
    const scoreText =
      bestScore !== null
        ? String(bestScore)
        : latestScore !== null
        ? String(latestScore)
        : ''

    await downloadCertificate(
      certificateName,
      undefined,
      undefined,
      certificateName,
      course?.title,
      true,
      course?.id,
      undefined,
      hasPosttest,
      scoreText,
      course?.min_passing_score || '',
      certificateMeta,
    )
  }

  const confirmCertificateDownload = async () => {
    setShowCertificateModal(false)

    const bestScore = await getBestCbtScore()
    const scoreText =
      bestScore !== null
        ? String(bestScore)
        : latestScore !== null
        ? String(latestScore)
        : ''

    const name = certificateInput || course?.title || 'Course Certificate'
    setStoredCertificateName(course?.id, name)
    setStoredCertificateNameState(name)

    await downloadCertificate(
      name,
      undefined,
      undefined,
      name,
      course?.title,
      true,
      course?.id,
      undefined,
      hasPosttest,
      scoreText,
      course?.min_passing_score || '',
      certificateMeta,
    )
  }

  return (
    <div className="col-xl-4 col-md-6">
      <div
        className="course-item-two course-item mx-0"
        style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div className="course-img">
          <Link
            to={detailLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            <ImageWithBasePath
              src={''}
              srcApi={course.image || '/assets/img/course/course-48.jpg'}
              fallbackSrc="/assets/img/course/course-48.jpg"
              alt={course.title}
              className="img-fluid"
              isLandscape={true}
            />
          </Link>
          <div className="position-absolute start-0 top-0 d-flex align-items-start w-100 z-index-2 p-3">
            {course.discount && course.discount !== '0.00' && (
              <div className="badge text-bg-danger">
                {course.discount_type === 'PERCENTAGE'
                  ? `${course.discount}% off`
                  : `${rupiahFormatter(parseFloat(course.discount))} off`}
              </div>
            )}
            <Link
              to="#"
              className={`fav-icon ms-auto ${
                selectedItems[course.id] ? 'selected' : ''
              }`}
              onClick={() => handleItemClick(course.id)}
            >
              <i className="isax isax-heart" />
            </Link>
          </div>
        </div>
        <div
          className="course-content"
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div className="d-flex align-items-center">
              {course.start_date && course.end_date && type === 'live' && (
                <>
                  {(() => {
                    const now = new Date().getTime()
                    const startDate = new Date(course.start_date).getTime()
                    const endDate = new Date(course.end_date).getTime()

                    if (now > endDate) {
                      return (
                        <span className="badge mb-0 bg-info">
                          Event Selesai
                        </span>
                      )
                    }

                    if (now >= startDate && now <= endDate) {
                      return (
                        <span className="badge mb-0 bg-success">Live Now</span>
                      )
                    }

                    if (now < startDate) {
                      // Tampilkan format yang berbeda berdasarkan jumlah hari
                      let countdownText = ''
                      if (countdown.days > 0) {
                        countdownText = `${
                          countdown.days
                        } hari ${countdown.hours
                          .toString()
                          .padStart(
                            2,
                            '0',
                          )} : ${countdown.minutes
                          .toString()
                          .padStart(
                            2,
                            '0',
                          )} : ${countdown.seconds.toString().padStart(2, '0')}`
                      } else {
                        countdownText = `${countdown.hours
                          .toString()
                          .padStart(
                            2,
                            '0',
                          )} : ${countdown.minutes
                          .toString()
                          .padStart(
                            2,
                            '0',
                          )} : ${countdown.seconds.toString().padStart(2, '0')}`
                      }

                      return (
                        <span className="badge mb-0 bg-warning">
                          {countdownText}
                        </span>
                      )
                    }
                  })()}
                </>
              )}
              {type === 'course' && (
                <>
                  <span className="badge mb-0 bg-info">
                    {`${course.student_count || 0} Siswa`}
                  </span>
                </>
              )}
              {type === 'cbt' && (
                <>
                  <span className="badge mb-0 bg-info">
                    {`${course.questions_count || 0} Soal`}
                  </span>
                </>
              )}
            </div>
            <span className="badge badge-light rounded-pill bg-light d-inline-flex align-items-center fs-13 fw-medium mb-0">
              {course.course_level}
            </span>
          </div>
          <h6
            className="title mb-2"
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              lineHeight: '1.4',
              minHeight: '2.8em', // 2 baris x 1.4 line-height
              maxHeight: '2.8em',
            }}
          >
            <Link
              to={detailLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              {course.title}
            </Link>
          </h6>
          <div className="d-flex align-items-center">
            <div className="price">
              {course.discount && course.discount !== '0.00' ? (
                <h5>
                  {calculateDiscount(
                    parseFloat(course.price),
                    course.discount,
                    course.discount_type,
                  )}{' '}
                  <span>{rupiahFormatter(parseFloat(course.price))}</span>
                </h5>
              ) : (
                <h5>{rupiahFormatter(parseFloat(course.price))}</h5>
              )}
            </div>
          </div>
          <Link
            to={detailLink}
            className="btn btn-dark btn-sm d-inline-flex align-items-center mt-2 fit-content"
            style={{
              width: 'fit-content',
            }}
          >
            Lihat Detail
            <i className="isax isax-arrow-right-3 ms-1" />
          </Link>

          {hasPosttest && (
            <>
              <div className="d-flex flex-wrap gap-2 mt-2">
                {!hasAttempt && (
                  <Link
                    to={all_routes.studentCBTInstruction.replace(
                      ':id',
                      course.id.toString(),
                    )}
                    className="btn btn-primary btn-sm"
                  >
                    Mulai Ujian
                  </Link>
                )}
                {hasAttempt && canRetry && (
                  <Link
                    to={all_routes.studentCBTInstruction.replace(
                      ':id',
                      course.id.toString(),
                    )}
                    className="btn btn-warning btn-sm"
                  >
                    Ulangi Ujian
                  </Link>
                )}
                {latestAttemptId ? (
                  <Link
                    to={all_routes.studentCBTReview.replace(
                      ':id',
                      String(latestAttemptId),
                    )}
                    className="btn btn-outline-secondary btn-sm"
                  >
                    Lihat Pembahasan
                  </Link>
                ) : (
                  <span className="btn btn-outline-secondary btn-sm disabled">
                    Lihat Pembahasan
                  </span>
                )}
                <button
                  type="button"
                  className="btn btn-sm text-white fw-semibold shadow-sm border-0 d-inline-flex align-items-center gap-1"
                  disabled={isCertificateLocked}
                  style={{
                    backgroundColor: isCertificateLocked ? '#94a3b8' : '#22c55e',
                    cursor: isCertificateLocked ? 'not-allowed' : 'pointer',
                    opacity: isCertificateLocked ? 0.85 : 1,
                  }}
                  onClick={handleDownloadCertificate}
                >
                  <i className={`isax ${isCertificateLocked ? 'isax-lock' : 'isax-award'} me-1`} />
                  Download Sertifikat
                </button>
              </div>
              <div className="d-flex flex-wrap gap-2 mt-2">
                <span className="badge bg-light text-dark border">
                  Nilai:{' '}
                  {bestCbtScore !== null
                    ? bestCbtScore
                    : latestScore !== null
                    ? latestScore
                    : '-'}
                </span>
                {maxAttempt > 0 && remainingAttempts !== null ? (
                  <span
                    className={`badge ${
                      remainingAttempts === 0 ? 'bg-danger' : 'bg-info'
                    }`}
                  >
                    {remainingAttempts === 0
                      ? 'Kesempatan habis'
                      : `Sisa kesempatan: ${remainingAttempts}/${maxAttempt}`}
                  </span>
                ) : null}
                {hasAttempt ? (
                  <span
                    className={`badge ${isPassed ? 'bg-success' : 'bg-danger'}`}
                  >
                    {isPassed ? 'Lulus' : 'Tidak Lulus'}
                  </span>
                ) : (
                  <span className="badge bg-secondary">Belum Ujian</span>
                )}
                <span
                  className={`badge ${
                    !isCertificateLocked ? 'bg-success' : 'bg-secondary'
                  }`}
                  style={{ backgroundColor: !isCertificateLocked ? undefined : '#94a3b8' }}
                >
                  {!isCertificateLocked
                    ? 'Sertifikat tersedia'
                    : 'Sertifikat belum tersedia'}
                </span>
              </div>
            </>
          )}
        </div>
      </div>
      {showCertificateModal && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div className="modal fade show" style={{ display: 'block' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Download Sertifikat</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowCertificateModal(false)}
                  />
                </div>
                <div className="modal-body">
                  <p className="mb-3">
                    Apakah Anda yakin ingin mencetak e-sertifikat dengan nama
                    yang tertera pada formulir di bawah ini?
                  </p>
                  <div className="mb-3">
                    <label htmlFor="certificateName" className="form-label">
                      Nama pada Sertifikat
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="certificateName"
                      value={certificateInput}
                      onChange={(e) => setCertificateInput(e.target.value)}
                      placeholder="Masukkan nama lengkap"
                    />
                  </div>
                  <div className="alert alert-warning">
                    <i className="isax isax-warning-2 me-2" />
                    Pastikan nama sudah benar, karena e-sertifikat hanya dapat
                    di generate satu kali.
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowCertificateModal(false)}
                  >
                    Batal
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={confirmCertificateDownload}
                    disabled={!certificateInput.trim()}
                  >
                    Download Sertifikat
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

const StudentCourse = () => {
  const [selectedItems, setSelectedItems] = useState<{
    [key: number]: boolean
  }>({})

  // Fetch purchased courses data
  const { data: purchasedCoursesData, isLoading, error } = useQuery({
    queryKey: ['purchasedCourses'],
    queryFn: getPurchasedCourses,
  })

  const handleItemClick = (index: number) => {
    setSelectedItems((prevSelectedItems) => ({
      ...prevSelectedItems,
      [index]: !prevSelectedItems[index],
    }))
  }

  // Loading state
  if (isLoading) {
    return (
      <>
        <Breadcrumb title="Enrolled Courses" />
        <div className="content">
          <div className="container">
            <ProfileCard />
            <div className="row">
              <StudentSidebar />
              <div className="col-lg-9">
                <div
                  className="d-flex justify-content-center align-items-center"
                  style={{ minHeight: '400px' }}
                >
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }

  // Error state
  if (error) {
    return (
      <>
        <Breadcrumb title="Enrolled Courses" />
        <div className="content">
          <div className="container">
            <ProfileCard />
            <div className="row">
              <StudentSidebar />
              <div className="col-lg-9">
                <div className="alert alert-danger" role="alert">
                  Failed to load purchased courses. Please try again later.
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }

  const liveCourses = purchasedCoursesData?.data?.Live_Teaching || []
  const regularCourses = purchasedCoursesData?.data?.Course || []
  const cbtCourses = purchasedCoursesData?.data?.CBT || []

  return (
    <>
      <Breadcrumb title="Enrolled Courses" />

      <div className="content">
        <div className="container">
          {/* profile box */}
          <ProfileCard />
          {/* profile box */}
          <div className="row">
            {/* sidebar */}
            <StudentSidebar />
            {/* sidebar */}
            <div className="col-lg-9">
              <div className="page-title d-flex flex-wrap gap-3 align-items-center justify-content-between">
                <h5>Enrolled Courses</h5>
                <div className="tab-list">
                  <ul className="nav mb-0 gap-2" role="tablist">
                    <li className="nav-item mb-0" role="presentation">
                      <Link
                        to="#"
                        className="active"
                        data-bs-toggle="tab"
                        data-bs-target="#enroll-courses"
                        aria-selected="true"
                        role="tab"
                      >
                        Live ({liveCourses.length})
                      </Link>
                    </li>
                    <li className="nav-item mb-0" role="presentation">
                      <Link
                        to="#"
                        data-bs-toggle="tab"
                        data-bs-target="#active-courses"
                        aria-selected="false"
                        role="tab"
                        className=""
                        tabIndex={-1}
                      >
                        Course ({regularCourses.length})
                      </Link>
                    </li>
                    <li className="nav-item mb-0" role="presentation">
                      <Link
                        to="#"
                        data-bs-toggle="tab"
                        data-bs-target="#complete-courses"
                        aria-selected="false"
                        role="tab"
                        className=""
                        tabIndex={-1}
                      >
                        CBT ({cbtCourses.length})
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="tab-content">
                <div
                  className="tab-pane fade active show"
                  id="enroll-courses"
                  role="tabpanel"
                >
                  <div className="row">
                    {liveCourses.length > 0 ? (
                      liveCourses.map((course) => (
                        <CourseCard
                          key={course.id}
                          course={course}
                          selectedItems={selectedItems}
                          handleItemClick={handleItemClick}
                          type="live"
                        />
                      ))
                    ) : (
                      <div className="col-12">
                        <div className="text-center py-5">
                          <p className="text-muted">
                            No live courses enrolled yet.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div
                  className="tab-pane fade"
                  id="active-courses"
                  role="tabpanel"
                >
                  <div className="row">
                    {regularCourses.length > 0 ? (
                      regularCourses.map((course) => (
                        <CourseCard
                          key={course.id}
                          course={course}
                          selectedItems={selectedItems}
                          handleItemClick={handleItemClick}
                          type="course"
                        />
                      ))
                    ) : (
                      <div className="col-12">
                        <div className="text-center py-5">
                          <p className="text-muted">No courses enrolled yet.</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div
                  className="tab-pane fade"
                  id="complete-courses"
                  role="tabpanel"
                >
                  <div className="row">
                    {cbtCourses.length > 0 ? (
                      cbtCourses.map((course) => (
                        <CourseCard
                          key={course.id}
                          course={course}
                          selectedItems={selectedItems}
                          handleItemClick={handleItemClick}
                          type="cbt"
                        />
                      ))
                    ) : (
                      <div className="col-12">
                        <div className="text-center py-5">
                          <p className="text-muted">
                            No CBT courses enrolled yet.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {/* /pagination */}
              {/* <div className="row align-items-center mt-2">
                <div className="col-md-2">
                  <p className="pagination-text">Page 1 of 1</p>
                </div>
                <div className="col-md-10">
                  <ul className="pagination lms-page justify-content-center justify-content-md-end mt-2 mt-md-0">
                    <li className="page-item prev">
                      <Link className="page-link" to="#" tabIndex={-1}>
                        <i className="fas fa-angle-left" />
                      </Link>
                    </li>
                    <li className="page-item first-page active">
                      <Link className="page-link" to="#">
                        1
                      </Link>
                    </li>
                    <li className="page-item next">
                      <Link className="page-link" to="#">
                        <i className="fas fa-angle-right" />
                      </Link>
                    </li>
                  </ul>
                </div>
              </div> */}
              {/* /pagination */}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default StudentCourse
